
import { Dialog, DialogContent } from "@/components/ui/dialog";
import EditDialogContent from "./edit-dialog/EditDialogContent";
import { useEditContentData } from "@/hooks/useEditContentData";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface EditContentDialogProps {
  contentId: string;
  title: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditContentDialog = ({
  contentId,
  title,
  isOpen,
  onOpenChange,
}: EditContentDialogProps) => {
  // Only fetch data when the dialog is actually open
  const { contentData, isLoading, error } = useEditContentData(contentId, isOpen);
  const prevIsOpenRef = useRef(isOpen);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  console.log('[EditContentDialog] rendered with props:', { 
    contentId, 
    title, 
    isOpen,
    hasData: !!contentData
  });
  
  // Log when the dialog is opened to help with debugging
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      console.log('[EditContentDialog] opened for content:', contentId, title);
      setHasAttemptedFetch(true);
    } else if (!isOpen && prevIsOpenRef.current) {
      console.log('[EditContentDialog] closed for content:', contentId);
      
      // TEMPORARY FIX: Force page refresh when dialog is closed - no navigation
      setTimeout(() => {
        console.log('[EditContentDialog] Forcing page refresh after dialog close');
        window.location.reload();
      }, 100);
    }
    
    prevIsOpenRef.current = isOpen;
  }, [isOpen, contentId, title]);

  // Handle errors from data fetching
  useEffect(() => {
    if (error && hasAttemptedFetch) {
      console.error('[EditContentDialog] Error fetching content data:', error);
      toast.error('Failed to load content data for editing');
    }
  }, [error, hasAttemptedFetch]);

  // Handle dialog state changes - simplified to avoid UI freezes
  const handleOpenChange = (open: boolean) => {
    console.log('[EditContentDialog] handleOpenChange called with:', open);
    onOpenChange(open);
  };

  // Safely handle dialog completion
  const handleComplete = () => {
    console.log('[EditContentDialog] onComplete called, closing dialog');
    onOpenChange(false);
    
    // TEMPORARY FIX: Force page refresh after dialog completion - no navigation
    setTimeout(() => {
      console.log('[EditContentDialog] Forcing page refresh after completion');
      window.location.reload();
    }, 100);
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={handleOpenChange}
    >
      <DialogContent 
        className="sm:max-w-[550px]"
        onInteractOutside={(e) => {
          // Prevent handling if clicking inside dropdown menus or context menus
          if (e.target && 
              ((e.target as HTMLElement).closest('[role="menu"]') || 
               (e.target as HTMLElement).closest('.dropdown-menu'))) {
            e.preventDefault();
          }
        }}
      >
        <EditDialogContent
          isLoading={isLoading}
          contentData={contentData}
          contentId={contentId}
          onComplete={handleComplete}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditContentDialog;
